import { useState } from 'react';
import type { JawOutline } from '@witmetrics/utils';
import type { ServiceSettings } from '@witmetrics/unison-core';
import { useScanService } from '@witmetrics/data-store';
import { isEmpty, isNullish } from '@witmetrics/utils/misc';
import { formatNumber, isString } from '@witmetrics/utils/strings';
import { isNumber } from '@witmetrics/utils/numbers';
import { getParameterValue } from '@witmetrics/utils/scanServices';
import { useAppState } from '@/providers/AppStateProvider';
import { useString } from '@/hooks/useString';
import { useMountEffect } from '@/hooks/useMountEffect';
import JawOutlineDialog from '@/dialogs/JawOutlineDialog';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import SearchField from '@/components/inputs/SearchField';
import { Tab } from '@/components/TabsBlock';
import { classes, filterSettings } from './utils';

export type SettingsTabProps = {
  isFetchingData: boolean;
  name: string;
  label: string;
  serviceSettings?: ServiceSettings;
  onFetchServiceSettings: () => void;
};

export default function SettingsTab({
  isFetchingData,
  name,
  label,
  serviceSettings,
  onFetchServiceSettings,
}: SettingsTabProps) {
  const { activeServiceId } = useAppState();
  const scanService = useScanService(activeServiceId);
  const [activeSettings, setActiveSettings] = useState<{
    label: string;
    value: JawOutline;
  } | null>(null);
  const [search, setSearch] = useString();

  useMountEffect(() => {
    if (!serviceSettings) onFetchServiceSettings(); // Only fetch settings from Relu when tab is opened
  });

  const renderParameter = (key: string, value: any) => {
    const paramValue = getParameterValue(scanService!.type, key, value);
    if (isNullish(paramValue)) return '--';
    if (isNumber(paramValue)) return formatNumber(paramValue);
    if (Array.isArray(paramValue)) return (value as string[]).join(', ');
    if (isString(paramValue)) return paramValue;
    // It's a JawOutline value, too large to nicely render inline
    return (
      <button
        className={classes.viewButton}
        onClick={() => setActiveSettings({ label, value })}>
        Show Values
      </button>
    );
  };

  const renderSettings = () => {
    if (isFetchingData) {
      return (
        <div className={classes.loading}>
          Loading settings
          <LoopingEllipsis />
        </div>
      );
    } else if (!serviceSettings) {
      return <div className={classes.noSettings}>No settings available.</div>;
    }
    const filteredSettings = filterSettings(serviceSettings, search);
    if (isEmpty(filteredSettings)) {
      return <div className={classes.noSettings}>No matching results</div>;
    } else {
      return (
        <ul>
          {filteredSettings.map(({ key, label, value }) => (
            <li key={label} className={classes.listItem}>
              <b>{label}</b>
              <div>{renderParameter(key, value)}</div>
            </li>
          ))}
        </ul>
      );
    }
  };

  if (!scanService) return null;

  return (
    <>
      {activeSettings && (
        <JawOutlineDialog
          title={activeSettings.label}
          settings={activeSettings.value}
          onClose={() => setActiveSettings(null)}
        />
      )}
      <Tab className={classes.tab} name={name} label={label}>
        <SearchField
          className={classes.searchField}
          value={search}
          onChange={setSearch}
        />
        {renderSettings()}
      </Tab>
    </>
  );
}
